import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import { WaiverDocument } from '../../../interfaces/collection.interface';
import { ConsentEvidenceService } from '../../../services/certificate/consent-evidence.service';

export type WaiverSigningState = 'idle' | 'submitting' | 'success';

@Component({
  selector: 'lib-waiver-dialog',
  imports: [
    CommonModule,
    TranslocoModule,
    MzicSvgComponent,
    SpinnerComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './waiver-dialog.component.html',
  styleUrl: './waiver-dialog.component.scss',
})
export class WaiverDialogComponent implements OnInit, OnDestroy {
  private readonly translocoService = inject(TranslocoService);
  private readonly consentEvidenceService = inject(ConsentEvidenceService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() waivers: WaiverDocument[] = [];
  @Input() userName = '';
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() signingState: WaiverSigningState = 'idle';
  @Input() loading = false;

  @Output() submitRequested = new EventEmitter<void>();
  @Output() cancelRequested = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  isAccepted = signal(false);

  ngOnInit(): void {
    // Start sensor tracking when dialog opens
    this.consentEvidenceService.startEvidenceCollection();
  }

  ngOnDestroy(): void {
    // Stop sensor tracking when dialog closes
    this.consentEvidenceService.stopEvidenceCollection();
  }

  toggleAcceptance(): void {
    this.isAccepted.set(!this.isAccepted());
  }

  viewDocument(waiver: WaiverDocument): void {
    if (waiver.document_url) {
      window.open(waiver.document_url, '_blank', 'noopener');
    }
    // Track document view for audit trail
    this.consentEvidenceService.trackDocumentView();
  }

  onSign(): void {
    if (this.canSign) {
      // Track consent click for audit trail
      this.consentEvidenceService.trackConsentClick();
      // Emit submit event - parent handles the actual signing
      this.submitRequested.emit();
    }
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  onBack(): void {
    this.back.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  get canSign(): boolean {
    return (
      this.isAccepted() &&
      this.signingState !== 'submitting' &&
      !this.loading &&
      this.waivers.length > 0
    );
  }

  get waiverCount(): number {
    return this.waivers.length;
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get waiverDialogSubtitleKey(): string {
    return `lang.waiver_dialog_subtitle${this.integrationSuffix}`;
  }

  getWaiverLabel(waiver: WaiverDocument): string {
    const typeMap: Record<string, string> = {
      DATA_CONNECTOR_CONNECTION_WAIVER_INDIVIDUAL:
        'waiver_type_data_connector_collection',
      DATA_CONNECTOR_CONNECTION_WAIVER_BUSINESS:
        'waiver_type_data_connector_collection',
      DATA_CONNECTOR_CONNECTION_REPRESENTATIVE_WAIVER:
        'waiver_type_representative_declaration',
    };
    const key = typeMap[waiver.operation_type] || 'waiver_type_default';
    if (
      this.integrationEnabled &&
      (key === 'waiver_type_data_connector_collection' ||
        key === 'waiver_type_default')
    ) {
      return `${key}_integration`;
    }
    return key;
  }

  get shimmerItems(): number[] {
    return Array.from({ length: 2 }, (_, i) => i);
  }

  get waiverViewDocumentLabel(): string {
    return this.translocoService.translate(
      'waiver_view_document',
      {},
      'mzic-collection-flow',
    );
  }
}
